/**
 * Dialog Node Utilities
 * 
 * Shared types, interfaces, and helper functions for dialog flow nodes
 */

import { DialogField, DialogNode, DialogOption, ConditionOption, DialogVariable } from '@/lib/stores/dialogStore';

// Color scheme type for node styling
export interface NodeColorScheme {
  bg: string;
  border: string;
  text: string;
  shadowColor: string; // Hex color for shadow
}

// Node info for resolving node names (new structure)
export interface NodeInfo {
  node_identifier: string;
  name?: string;
}

// Section info for resolving section names (legacy)
export interface SectionInfo {
  section_identifier: string;
  name?: string;
}

// Base data interface shared by all dialog field nodes
export interface DialogFieldNodeData {
  // New structure
  node?: DialogNode;
  nodeName?: string;
  nodeIndex?: number;
  nodes?: NodeInfo[];
  onNodeUpdate?: (nodeIndex: number, updates: Partial<DialogNode>) => void;
  
  // Legacy structure (for backward compatibility)
  field?: DialogField;
  sectionName?: string;
  sectionIndex?: number;
  fieldIndex?: number;
  sections?: SectionInfo[];
  onFieldUpdate?: (sectionIndex: number, fieldIndex: number, updates: Partial<DialogField>) => void;
  onSectionUpdate?: (sectionIndex: number, updates: { name: string }) => void;
  
  // Common
  label: string;
  variables?: DialogVariable[];
  [key: string]: any;
}

// Props passed to each specific node component from the base
export interface DialogNodeContentProps {
  node?: DialogNode;
  field?: DialogField;
  isEditing: boolean;
  onStartEditing: () => void;
  onSaveChanges: (updates: Partial<DialogNode>) => void;
  onCancelEditing: () => void;
  getNodeName: (identifier: string | null | undefined) => string;
  getSectionName?: (identifier: string | null | undefined) => string; // Legacy
}

/**
 * Get the Font Awesome icon string for a field type
 */
export function getFieldTypeIcon(type: string): string {
  switch (type) {
    case 'question': return 'fa-circle-question';
    case 'context': return 'fa-circle-info';
    case 'condition': return 'fa-code-branch';
    case 'setter': return 'fa-pen-to-square';
    case 'external-dialog': return 'fa-arrow-right-to-bracket';
    case 'end-text': // Legacy type, redirect to end-dialog
    case 'end-dialog': return 'fa-circle-stop';
    default: return 'fa-comment';
  }
}

/**
 * Get the color scheme for a field type
 */
export function getFieldTypeColor(type: string): NodeColorScheme {
  switch (type) {
    case 'question': return { bg: 'bg-purple-50', border: 'border-purple-300', text: 'text-purple-700', shadowColor: '#c084fc' };
    case 'context': return { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-700', shadowColor: '#60a5fa' };
    case 'condition': return { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700', shadowColor: '#fb923c' };
    case 'setter': return { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-700', shadowColor: '#4ade80' };
    case 'external-dialog': return { bg: 'bg-cyan-50', border: 'border-cyan-300', text: 'text-cyan-700', shadowColor: '#22d3ee' };
    case 'end-text': // Legacy type, redirect to end-dialog
    case 'end-dialog': return { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700', shadowColor: '#f87171' };
    default: return { bg: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-700', shadowColor: '#9ca3af' };
  }
}

/**
 * Get node name by identifier from nodes array (new structure)
 */
export function getNodeName(identifier: string | null | undefined, nodes?: NodeInfo[]): string {
  if (!identifier) return '';
  const node = nodes?.find(n => n.node_identifier === identifier);
  return node?.name || identifier;
}

/**
 * Get section name by identifier from sections array (legacy)
 */
export function getSectionName(identifier: string | null | undefined, sections?: SectionInfo[]): string {
  if (!identifier) return '';
  const section = sections?.find(s => s.section_identifier === identifier);
  return section?.name || identifier;
}

/**
 * Get display label for a field type
 */
export function getFieldTypeLabel(type: string): string {
  return type.replace('-', ' ');
}
